/**
 * 대시보드 — / 경로의 홈 화면.
 *
 * 레퍼런스(I'mware 메인) 의 4컬럼 구조를 따른다:
 *
 *   ┌─────────┬──────────────┬──────────────┬─────────────┐
 *   │  내정보  │   결재함     │  채널 신규글  │ 신규 입사자  │
 *   │ (hero)  │   안 읽은 메일│  공지사항    │ 다가오는 생일│
 *   └─────────┴──────────────┴──────────────┴─────────────┘
 *
 * 위젯 헤더는 큰 굵은 제목 + 우측 chip-link("전체보기 ›") 패턴.
 * 각 행은 좌측 상태 chip + 가운데 제목/메타 + 우측 날짜.
 * 헤더 또는 행 클릭 시 사이드바의 해당 모듈로 이동.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
    Avatar, Box, Button, Chip, Divider, Grid, Paper, Stack, Typography,
} from "@mui/material";
import CampaignRoundedIcon       from "@mui/icons-material/CampaignRounded";
import AssignmentTurnedInRoundedIcon from "@mui/icons-material/AssignmentTurnedInRounded";
import AccountTreeRoundedIcon    from "@mui/icons-material/AccountTreeRounded";
import LockResetRoundedIcon      from "@mui/icons-material/LockResetRounded";
import EmailRoundedIcon          from "@mui/icons-material/EmailRounded";
import ChevronRightRoundedIcon   from "@mui/icons-material/ChevronRightRounded";
import CakeRoundedIcon           from "@mui/icons-material/CakeRounded";
import HowToRegRoundedIcon       from "@mui/icons-material/HowToRegRounded";
import InsightsRoundedIcon       from "@mui/icons-material/InsightsRounded";
import CheckCircleRoundedIcon    from "@mui/icons-material/CheckCircleRounded";
import AutoAwesomeRoundedIcon    from "@mui/icons-material/AutoAwesomeRounded";
import ScheduleRoundedIcon       from "@mui/icons-material/ScheduleRounded";
import MarkEmailReadRoundedIcon  from "@mui/icons-material/MarkEmailReadRounded";
import dayjs                     from "dayjs";

import { api } from "../../util/api";
import ActionButton from "../../component/common/ActionButton";
import PasswordChangeModal from "./PasswordChangeModal";
/* 로그인 안내 모달(LoginAnnouncementModal)과 같은 현황을 말해야 하므로 목록은 한 파일에서만 관리한다. */
import { FEEDBACK_DONE, FEEDBACK_PENDING, MAIL_RESTORE_NOTICE } from "../../config/feedbackStatus";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/* ─── 색 토큰 — 위젯별 강조색 ────────────────────────────────── */
const C = {
    approval:  { main: "#2563eb", soft: "rgba(37,99,235,0.10)" },
    mail:      { main: "#0ea5e9", soft: "rgba(14,165,233,0.10)" },
    project:   { main: "#7c3aed", soft: "rgba(124,58,237,0.10)" },
    notice:    { main: "#dc2626", soft: "rgba(220,38,38,0.10)" },
    join:      { main: "#16a34a", soft: "rgba(22,163,74,0.10)" },
    birthday:  { main: "#f59e0b", soft: "rgba(245,158,11,0.12)" },
};

const STATUS_COLOR = {
    PENDING:     { main: "#2563eb", bg: "rgba(37,99,235,0.12)" },
    IN_PROGRESS: { main: "#2563eb", bg: "rgba(37,99,235,0.12)" },
    APPROVED:    { main: "#475569", bg: "rgba(71,85,105,0.10)" },
    REJECTED:    { main: "#dc2626", bg: "rgba(220,38,38,0.12)" },
    HOLD:        { main: "#f59e0b", bg: "rgba(245,158,11,0.14)" },
    DRAFT:       { main: "#64748b", bg: "rgba(100,116,139,0.12)" },
    DEFAULT:     { main: "#475569", bg: "rgba(71,85,105,0.10)" },
};

function safeDate(v) {
    if (!v) return "-";
    const d = dayjs(v);
    return d.isValid() ? d.format("YYYY-MM-DD") : "-";
}
function daysSince(dateStr) {
    if (!dateStr) return null;
    const d = dayjs(dateStr);
    if (!d.isValid()) return null;
    return dayjs().startOf("day").diff(d.startOf("day"), "day");
}
function fmtRel(t) {
    if (!t) return "";
    const d = dayjs(t);
    if (!d.isValid()) return "";
    const diff = dayjs().diff(d, "minute");
    if (diff < 1)            return "방금";
    if (diff < 60)           return `${diff}분 전`;
    if (diff < 60 * 24)      return `${Math.floor(diff / 60)}시간 전`;
    if (diff < 60 * 24 * 7)  return `${Math.floor(diff / (60 * 24))}일 전`;
    return d.format("MM-DD");
}

/* ─── 위젯 카드 — 헤더 + 본문. accent 는 좌측 컬러 띠로 표현 ───── */
function WidgetCard({ accent, icon, title, count, onTitleClick, children }) {
    const hasCount   = typeof count === "number";
    const isZero     = hasCount && count === 0;
    return (
        <Paper sx={{
            borderRadius: 1.5,
            border: "1px solid #eef0f4",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            display: "flex", flexDirection: "column",
            height: "100%", minHeight: 200,
            overflow: "hidden", bgcolor: "#fff",
            transition: "transform 0.16s ease, box-shadow 0.16s ease",
            "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 6px 18px rgba(15,23,42,0.08)",
            },
        }}>
            <Stack
                direction="row" alignItems="center" gap={1}
                onClick={onTitleClick}
                sx={{
                    px: 2, py: 1.55,
                    borderLeft: `3px solid ${accent}`,
                    cursor: onTitleClick ? "pointer" : "default",
                    "&:hover .arrow": onTitleClick ? { transform: "translateX(2px)", color: accent } : {},
                }}>
                {icon && (
                    <Box sx={{
                        width: 26, height: 26, borderRadius: 1.2,
                        bgcolor: `${accent}1a`, color: accent,
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                    }}>
                        {icon}
                    </Box>
                )}
                <Typography sx={{
                    flex: 1, fontSize: 15, fontWeight: 800, color: "#0f172a",
                    letterSpacing: "-0.01em",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                    {title}
                </Typography>
                {hasCount && (
                    <Box sx={{
                        fontSize: 11, fontWeight: 800,
                        px: 0.9, py: 0.2, borderRadius: 99,
                        bgcolor: isZero ? "#f1f5f9" : `${accent}1a`,
                        color:   isZero ? "#94a3b8" : accent,
                    }}>
                        {count}
                    </Box>
                )}
                {onTitleClick && (
                    <Stack className="arrow" direction="row" alignItems="center"
                        sx={{
                            color: "#94a3b8", fontSize: 11, fontWeight: 700,
                            transition: "all 0.12s",
                        }}>
                        <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
                    </Stack>
                )}
            </Stack>
            <Divider sx={{ borderColor: "#f1f5f9" }} />
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", py: 0.5 }}>
                {children}
            </Box>
        </Paper>
    );
}

/* ─── 행 (image #40 의 row 디자인) ───────────────────────────
 *   [상태칩]  제목                                  날짜
 *            서브텍스트(작성자/부서)
 */
function Row({ status, statusColor, primary, secondary, right, onClick, leftIcon }) {
    return (
        <Box onClick={onClick} sx={{
            display: "flex", alignItems: "center", gap: 1.2,
            px: 2, py: 1.1,
            cursor: onClick ? "pointer" : "default",
            borderBottom: "1px solid #f4f6fa",
            "&:last-of-type": { borderBottom: "none" },
            "&:hover": onClick ? { bgcolor: "#f8fafc" } : {},
        }}>
            {/* 좌측 상태 chip — image #40 의 [완료]/[진행]/[반려] 느낌 */}
            {status && (
                <Box sx={{
                    flexShrink: 0,
                    minWidth: 44,
                    height: 22, px: 0.9, borderRadius: 1,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 800,
                    bgcolor: statusColor?.bg, color: statusColor?.main,
                }}>
                    {status}
                </Box>
            )}
            {leftIcon}

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{
                    fontSize: 13.5, fontWeight: 600, color: "#0f172a", lineHeight: 1.4,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                    {primary}
                </Typography>
                {secondary && (
                    <Typography sx={{
                        fontSize: 11.5, color: "#94a3b8", mt: 0.2,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                        {secondary}
                    </Typography>
                )}
            </Box>

            {right && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", flexShrink: 0, fontWeight: 500 }}>
                    {right}
                </Typography>
            )}
        </Box>
    );
}

/* ─── 홈 본문 — 대시보드 준비 안내 + 피드백 반영 현황 ─── */
function HomeNotice({ name, onGoFeedback }) {
    // maxWidth + mx:auto — 넓은 화면에서도 본문이 좌측에 붙지 않고 가운데에 온다.
    return (
        <Box sx={{ py: 3, px: { xs: 1, md: 2 }, maxWidth: 900, width: "100%", mx: "auto" }}>
            {/* 페이지 타이틀 */}
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
                    홈
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                    {name ? `${name}님, ` : ""}안녕하세요. 온앤미 통합 시스템입니다.
                </Typography>
            </Box>

            {/* 0) 메일 시스템 복구 안내 — 지금 당장 직원의 행동이 바뀌는 유일한 항목이라 맨 위에 둔다.
                   (문구는 로그인 안내 모달과 같은 상수를 쓴다) */}
            <Paper sx={{
                borderRadius: 1.5, border: "1px solid #bbf7d0", bgcolor: "#f0fdf4",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2,
            }}>
                <Stack direction="row" alignItems="flex-start" gap={1.2} sx={{ px: 2, py: 1.75 }}>
                    <MarkEmailReadRoundedIcon sx={{ fontSize: 20, color: "#16a34a", flexShrink: 0, mt: "2px" }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: "#166534", lineHeight: 1.5 }}>
                            {MAIL_RESTORE_NOTICE.title}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#475569", mt: 0.4, lineHeight: 1.7 }}>
                            {MAIL_RESTORE_NOTICE.lines.map((l, i) => (
                                <React.Fragment key={i}>{l}<br /></React.Fragment>
                            ))}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>

            {/* 1) 대시보드 준비 안내 */}
            <Paper sx={{
                borderRadius: 1.5, border: "1px solid #eef0f4", bgcolor: "#fff",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2,
            }}>
                <Stack direction="row" alignItems="center" gap={1}
                    sx={{ px: 2, py: 1.55, borderLeft: `3px solid ${C.approval.main}` }}>
                    <Box sx={{
                        width: 26, height: 26, borderRadius: 1.2,
                        bgcolor: C.approval.soft, color: C.approval.main,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                        <InsightsRoundedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                        대시보드 준비 중
                    </Typography>
                </Stack>
                <Divider sx={{ borderColor: "#f1f5f9" }} />
                <Box sx={{ px: 2, py: 2 }}>
                    <Typography sx={{ fontSize: 14, color: "#475569", lineHeight: 1.8 }}>
                        사용자분들의 의견을 수렴하여 <b>대시보드 내 데이터들을 표시할 예정</b>입니다. <br />
                        팀별로 원하시는 항목이 있으시다면 <b>피드백으로 언제든지 말씀해 주셨으면</b> 합니다.
                    </Typography>
                    <ActionButton
                        variant="secondary"
                        onClick={onGoFeedback}
                        startIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
                        label="피드백 남기러 가기"
                        sx={{ mt: 1.75, fontSize: 12.5 }}
                    />
                </Box>
            </Paper>

            {/* 2) 온앤미 인포 — 피드백 완료건 */}
            <Paper sx={{
                borderRadius: 1.5, border: "1px solid #eef0f4", bgcolor: "#fff",
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden",
            }}>
                <Stack direction="row" alignItems="center" gap={1}
                    sx={{ px: 2, py: 1.55, borderLeft: `3px solid ${C.join.main}` }}>
                    <Box sx={{
                        width: 26, height: 26, borderRadius: 1.2,
                        bgcolor: C.join.soft, color: C.join.main,
                        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                        <AutoAwesomeRoundedIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Typography sx={{ flex: 1, fontSize: 15, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" }}>
                        온앤미 인포 · 피드백 현황
                    </Typography>
                    <Box sx={{
                        fontSize: 11, fontWeight: 800, px: 0.9, py: 0.2, borderRadius: 99,
                        bgcolor: C.join.soft, color: C.join.main,
                    }}>
                        {FEEDBACK_DONE.length + FEEDBACK_PENDING.length}건
                    </Box>
                </Stack>
                <Divider sx={{ borderColor: "#f1f5f9" }} />

                {/* 반영 완료 */}
                <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, pt: 1.3, pb: 0.2 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: C.join.main }} />
                    <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 800, color: "#0f172a" }}>
                        반영 완료
                    </Typography>
                    <Box sx={{
                        fontSize: 11, fontWeight: 800, px: 0.9, py: 0.2, borderRadius: 99,
                        bgcolor: C.join.soft, color: C.join.main,
                    }}>
                        {FEEDBACK_DONE.length}건
                    </Box>
                </Stack>

                <Stack sx={{ px: 2, py: 1.5 }} gap={1.1}>
                    {FEEDBACK_DONE.map((f, i) => (
                        <Stack key={f.name} direction="row" alignItems="flex-start" gap={1}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 16, color: C.join.main, mt: "3px", flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.5 }}>
                                    {i + 1}. {f.name}
                                </Typography>
                                <Typography sx={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.55 }}>
                                    {f.desc}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Stack>

                {/* 진행 중·예정 — 완료건만 보여주면 "나머지는 안 하나" 로 읽힌다.
                    같은 카드 안에 이어 붙여, 무엇이 끝났고 무엇이 도는 중인지 한 번에 보이게 한다. */}
                <Divider sx={{ borderColor: "#f1f5f9" }} />
                <Stack direction="row" alignItems="center" gap={1} sx={{ px: 2, py: 1.3 }}>
                    <ScheduleRoundedIcon sx={{ fontSize: 16, color: "#d97706" }} />
                    <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 800, color: "#0f172a" }}>
                        진행 중 · 예정
                    </Typography>
                    <Box sx={{
                        fontSize: 11, fontWeight: 800, px: 0.9, py: 0.2, borderRadius: 99,
                        bgcolor: "#fffbeb", color: "#d97706",
                    }}>
                        {FEEDBACK_PENDING.length}건
                    </Box>
                </Stack>

                <Stack sx={{ px: 2, pb: 1.5 }} gap={1.1}>
                    {FEEDBACK_PENDING.map((f) => (
                        <Stack key={f.name} direction="row" alignItems="flex-start" gap={1}>
                            <ScheduleRoundedIcon sx={{ fontSize: 16, color: "#d97706", mt: "3px", flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.5 }}>
                                        {f.name}
                                    </Typography>
                                    {f.eta && (
                                        <Box sx={{
                                            fontSize: 11, fontWeight: 700, color: "#d97706",
                                            border: "1px solid #d97706", borderRadius: 99,
                                            px: 0.75, py: "1px", whiteSpace: "nowrap",
                                        }}>
                                            {f.eta}
                                        </Box>
                                    )}
                                </Stack>
                                <Typography sx={{ fontSize: 12.5, color: "#64748b", lineHeight: 1.55 }}>
                                    {f.desc}
                                </Typography>
                            </Box>
                        </Stack>
                    ))}
                </Stack>

                <Divider sx={{ borderColor: "#f1f5f9" }} />
                <Box sx={{ px: 2, py: 1.75, bgcolor: "#fafbfc" }}>
                    <Typography sx={{ fontSize: 13, color: "#475569", lineHeight: 1.7 }}>
                        위 항목들을 진행하고 있으며 <b>지속적으로 업데이트</b>되고 있으니,
                        필요하신 것이 있으시다면 언제든 말씀해 주세요.
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}

function EmptyState({ icon, label, hint }) {
    return (
        <Box sx={{
            p: 3, height: "100%",
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            color: "#94a3b8", textAlign: "center",
        }}>
            {icon && (
                <Box sx={{
                    width: 44, height: 44, borderRadius: "50%",
                    bgcolor: "#f8fafc", color: "#cbd5e1",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    mb: 1.2,
                }}>
                    {icon}
                </Box>
            )}
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "#64748b" }}>{label}</Typography>
            {hint && (
                <Typography sx={{ fontSize: 11, color: "#cbd5e1", mt: 0.4 }}>{hint}</Typography>
            )}
        </Box>
    );
}


/* ─── 메인 ──────────────────────────────────────────────── */
export default function DashboardPage() {
    const history = useHistory();
    const [me, setMe] = useState(null);
    const [summary, setSummary] = useState(null);
    const [pwOpen, setPwOpen] = useState(false);

    useEffect(() => {
        api.get(`${API_BASE}/employees/me`).then(setMe).catch(() => {});
        api.get(`${API_BASE}/dashboard/summary`).then(setSummary).catch(() => setSummary(null));
    }, []);

    const joinDays = useMemo(() => daysSince(me?.joinDate), [me?.joinDate]);
    const goto = (path) => () => history.push(path);

    // 위젯 레이아웃(아래 주석)이 열리기 전까지는 안내 본문만 노출한다.
    return (
        <HomeNotice name={me?.name} onGoFeedback={() => history.push("/feedback")} />
        // <Box sx={{
        //     py: 3, px: { xs: 1, md: 2 },
        //     minHeight: "100%",
        //     background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 280px)",
        // }}>
        //     {/* 페이지 타이틀 */}
        //     <Box sx={{ mb: 2.5 }}>
        //         <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
        //             대시보드
        //         </Typography>
        //         <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
        //             {me?.name ? `${me.name}님, ` : ""}오늘의 사내 현황을 한눈에 확인하세요.
        //         </Typography>
        //     </Box>
        //
        //     <Grid container spacing={2}>
        //         {/* ── 1. 내 정보 (좌측 hero 카드) ── */}
        //         <Grid item xs={12} md={3}>
        //             <ProfileCard me={me} joinDays={joinDays} onChangePw={() => setPwOpen(true)} />
        //             <PasswordChangeModal
        //                 open={pwOpen}
        //                 email={me?.email}
        //                 onClose={() => setPwOpen(false)}
        //                 onAfterChange={({ requireLogout }) => {
        //                     if (requireLogout) window.location.replace("/");
        //                 }}
        //             />
        //         </Grid>
        //
        //         {/* ── 2~7. 6 위젯 — nested 3컬럼 2행 grid ── */}
        //         <Grid item xs={12} md={9}>
        //             <Grid container spacing={2}>
        //
        //                 {/* row1: 결재 / 채널 / 입사자 */}
        //                 <Grid item xs={12} sm={6} md={4}>
        //                     <WidgetCard
        //                         accent={C.approval.main}
        //                         icon={<AssignmentTurnedInRoundedIcon sx={{ fontSize: 16 }} />}
        //                         title="결재함"
        //                         count={summary?.approvals?.length ?? (summary ? 0 : null)}
        //                         onTitleClick={goto("/approval")}>
        //                         <ListBody
        //                             items={summary?.approvals}
        //                             emptyIcon={<AssignmentTurnedInRoundedIcon sx={{ fontSize: 22 }} />}
        //                             emptyLabel="결재할 문서가 없어요"
        //                             emptyHint="새 결재가 도착하면 여기에 표시됩니다"
        //                             render={(a) => {
        //                                 const sc = STATUS_COLOR[a.status] || STATUS_COLOR.DEFAULT;
        //                                 return (
        //                                     <Row
        //                                         key={a.id}
        //                                         status={koStatus(a.status)}
        //                                         statusColor={sc}
        //                                         primary={a.title || "(제목 없음)"}
        //                                         secondary={`${a.typeLabel || a.type || "-"} · ${a.submitterName || "-"}`}
        //                                         right={fmtRel(a.createdAt)}
        //                                         onClick={goto("/approval")}
        //                                     />
        //                                 );
        //                             }} />
        //                     </WidgetCard>
        //                 </Grid>
        //
        //                 <Grid item xs={12} sm={6} md={4}>
        //                     <WidgetCard
        //                         accent={C.project.main}
        //                         icon={<AccountTreeRoundedIcon sx={{ fontSize: 16 }} />}
        //                         title="프로젝트 채널"
        //                         count={summary?.channelFeeds?.length ?? (summary ? 0 : null)}
        //                         onTitleClick={goto("/channel")}>
        //                         <ListBody
        //                             items={summary?.channelFeeds}
        //                             emptyIcon={<AccountTreeRoundedIcon sx={{ fontSize: 22 }} />}
        //                             emptyLabel="새 글이 없어요"
        //                             emptyHint="가입한 채널에 글이 올라오면 표시됩니다"
        //                             render={(f) => (
        //                                 <Row
        //                                     key={f.feedId}
        //                                     status={shortChannelType(f.channelType)}
        //                                     statusColor={{ main: C.project.main, bg: C.project.soft }}
        //                                     primary={f.title || f.preview || "(내용 없음)"}
        //                                     secondary={`${f.channelName || "채널"} · ${f.authorName || "-"}`}
        //                                     right={fmtRel(f.createdAt)}
        //                                     onClick={goto(`/channel/${f.channelId || ""}`)}
        //                                 />
        //                             )} />
        //                     </WidgetCard>
        //                 </Grid>
        //
        //                 <Grid item xs={12} sm={6} md={4}>
        //                     <WidgetCard
        //                         accent={C.join.main}
        //                         icon={<HowToRegRoundedIcon sx={{ fontSize: 16 }} />}
        //                         title="신규 입사자"
        //                         count={summary?.recentJoins?.length ?? (summary ? 0 : null)}
        //                         onTitleClick={goto("/hr")}>
        //                         <ListBody
        //                             items={summary?.recentJoins}
        //                             emptyIcon={<HowToRegRoundedIcon sx={{ fontSize: 22 }} />}
        //                             emptyLabel="최근 입사자가 없어요"
        //                             emptyHint="최근 30일 내 입사자가 표시됩니다"
        //                             render={(p) => (
        //                                 <Row
        //                                     key={p.id}
        //                                     leftIcon={
        //                                         <Avatar sx={{
        //                                             width: 28, height: 28, fontSize: 11, fontWeight: 800,
        //                                             bgcolor: C.join.soft, color: C.join.main, flexShrink: 0,
        //                                         }}>
        //                                             {(p.name || "?").trim().slice(-2)}
        //                                         </Avatar>
        //                                     }
        //                                     primary={p.name}
        //                                     secondary={[p.deptName, p.positionName].filter(Boolean).join(" · ") || "—"}
        //                                     right={dayjs(p.date).format("MM-DD")}
        //                                     onClick={goto("/hr")}
        //                                 />
        //                             )} />
        //                     </WidgetCard>
        //                 </Grid>
        //
        //                 {/* row2: 메일 / 공지 / 생일 */}
        //                 <Grid item xs={12} sm={6} md={4}>
        //                     <WidgetCard
        //                         accent={C.mail.main}
        //                         icon={<EmailRoundedIcon sx={{ fontSize: 16 }} />}
        //                         title="안 읽은 메일"
        //                         count={summary?.unreadMails?.length ?? (summary ? 0 : null)}
        //                         onTitleClick={goto("/mail")}>
        //                         <ListBody
        //                             items={summary?.unreadMails}
        //                             emptyIcon={<EmailRoundedIcon sx={{ fontSize: 22 }} />}
        //                             emptyLabel="새 메일이 없어요"
        //                             emptyHint="모두 읽음 처리되었습니다"
        //                             render={(m) => (
        //                                 <Row
        //                                     key={m.id}
        //                                     leftIcon={
        //                                         <Box sx={{
        //                                             width: 8, height: 8, borderRadius: "50%",
        //                                             bgcolor: C.mail.main, flexShrink: 0,
        //                                         }} />
        //                                     }
        //                                     primary={m.subject || "(제목 없음)"}
        //                                     secondary={m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail}
        //                                     right={fmtRel(m.receivedAt)}
        //                                     onClick={goto("/mail")}
        //                                 />
        //                             )} />
        //                     </WidgetCard>
        //                 </Grid>
        //
        //                 <Grid item xs={12} sm={6} md={4}>
        //                     <WidgetCard
        //                         accent={C.notice.main}
        //                         icon={<CampaignRoundedIcon sx={{ fontSize: 16 }} />}
        //                         title="공지사항"
        //                         count={summary?.notices?.length ?? (summary ? 0 : null)}
        //                         onTitleClick={goto("/channel")}>
        //                         <ListBody
        //                             items={summary?.notices}
        //                             emptyIcon={<CampaignRoundedIcon sx={{ fontSize: 22 }} />}
        //                             emptyLabel="새 공지가 없어요"
        //                             emptyHint="공지 채널에 글이 올라오면 표시됩니다"
        //                             render={(f) => (
        //                                 <Row
        //                                     key={f.feedId}
        //                                     leftIcon={
        //                                         <CampaignRoundedIcon sx={{ fontSize: 16, color: C.notice.main, flexShrink: 0 }} />
        //                                     }
        //                                     primary={f.title || f.preview || "(내용 없음)"}
        //                                     secondary={`${f.channelName || "공지"} · ${f.authorName || "-"}`}
        //                                     right={fmtRel(f.createdAt)}
        //                                     onClick={goto(`/channel/${f.channelId || ""}`)}
        //                                 />
        //                             )} />
        //                     </WidgetCard>
        //                 </Grid>
        //
        //                 <Grid item xs={12} sm={6} md={4}>
        //                     <WidgetCard
        //                         accent={C.birthday.main}
        //                         icon={<CakeRoundedIcon sx={{ fontSize: 16 }} />}
        //                         title="다가오는 생일"
        //                         count={summary?.birthdays?.length ?? (summary ? 0 : null)}
        //                         onTitleClick={goto("/hr")}>
        //                         <ListBody
        //                             items={summary?.birthdays}
        //                             emptyIcon={<CakeRoundedIcon sx={{ fontSize: 22 }} />}
        //                             emptyLabel="다가오는 생일이 없어요"
        //                             emptyHint="30일 내 생일자가 표시됩니다"
        //                             render={(p) => (
        //                                 <Row
        //                                     key={p.id}
        //                                     leftIcon={
        //                                         <Box sx={{
        //                                             width: 28, height: 28, borderRadius: "50%",
        //                                             bgcolor: C.birthday.soft, color: C.birthday.main,
        //                                             display: "flex", alignItems: "center", justifyContent: "center",
        //                                             flexShrink: 0,
        //                                         }}>
        //                                             <CakeRoundedIcon sx={{ fontSize: 16 }} />
        //                                         </Box>
        //                                     }
        //                                     primary={p.name}
        //                                     secondary={[p.deptName, p.positionName].filter(Boolean).join(" · ") || "—"}
        //                                     right={dayjs(p.date).format("MM-DD")}
        //                                     onClick={goto("/hr")}
        //                                 />
        //                             )} />
        //                     </WidgetCard>
        //                 </Grid>
        //
        //             </Grid>
        //         </Grid>
        //     </Grid>
        // </Box>
    );
}

/* ─── 좌측 hero 프로필 카드 ─── */
function ProfileCard({ me, joinDays, onChangePw }) {
    return (
        <Paper sx={{
            borderRadius: 1.5, height: "100%", overflow: "hidden",
            position: "relative", color: "#fff",
            background: "linear-gradient(155deg, #4f8bff 0%, #2563eb 50%, #1e40af 100%)",
            boxShadow: "0 10px 24px rgba(37,99,235,0.22)",
        }}>
            {/* 배경 데코 — 부드러운 빛 */}
            <Box sx={{
                position: "absolute", top: -50, right: -40, width: 180, height: 180,
                borderRadius: "50%",
                background: "radial-gradient(circle at center, rgba(255,255,255,0.18), rgba(255,255,255,0))",
            }} />
            <Box sx={{
                position: "absolute", bottom: -60, left: -30, width: 160, height: 160,
                borderRadius: "50%",
                background: "radial-gradient(circle at center, rgba(255,255,255,0.10), rgba(255,255,255,0))",
            }} />

            <Box sx={{
                position: "relative",
                p: 2.5, height: "100%",
                display: "flex", flexDirection: "column",
            }}>
                {/* 상단 헤더 — 아바타 + 정보 가로 */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                    <Avatar sx={{
                        width: 56, height: 56,
                        bgcolor: "rgba(255,255,255,0.22)",
                        fontSize: 18, fontWeight: 800, color: "#fff",
                        border: "2px solid rgba(255,255,255,0.32)",
                        boxShadow: "0 4px 10px rgba(15,23,42,0.15)",
                        flexShrink: 0,
                    }}>
                        {(me?.name || "?").trim().slice(-2)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.2 }}>
                            <Typography sx={{
                                fontSize: 11, opacity: 0.88,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {me?.deptName || "소속 없음"}
                            </Typography>
                            {me?.isAdmin && (
                                <Chip size="small" label="관리자" sx={{
                                    height: 16, fontSize: 9.5, fontWeight: 800,
                                    bgcolor: "rgba(255,255,255,0.22)", color: "#fff",
                                }} />
                            )}
                        </Stack>
                        <Typography sx={{
                            fontSize: 16, fontWeight: 800, lineHeight: 1.2,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {me?.name || "—"}{" "}
                            <Box component="span" sx={{ fontSize: 12, fontWeight: 500, opacity: 0.9 }}>
                                {me?.positionName || ""}
                            </Box>
                        </Typography>
                        {me?.email && (
                            <Typography sx={{
                                fontSize: 10.5, opacity: 0.78, mt: 0.2,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {me.email}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                {/* 핵심 KV — glassmorphism 박스 (자연 높이) */}
                <Box sx={{
                    bgcolor: "rgba(255,255,255,0.10)",
                    borderRadius: 1.5, p: 1.5,
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.18)",
                }}>
                    <Stack spacing={1}>
                        <KvRow label="사번"     value={me?.employeeCode || "—"} />
                        <KvRow label="입사일"   value={
                            joinDays != null
                                ? <span>{safeDate(me?.joinDate)} <Box component="span" sx={{ opacity: 0.75, ml: 0.4 }}>+{joinDays}일</Box></span>
                                : safeDate(me?.joinDate)
                        } />
                        <KvRow label="생년월일" value={safeDate(me?.birthDate)} />
                    </Stack>
                </Box>

                <Box sx={{ flex: 1 }} />

                <ActionButton
                    variant="primary"
                    startIcon={<LockResetRoundedIcon sx={{ fontSize: 14 }} />}
                    onClick={onChangePw}
                    fullWidth
                    label="비밀번호 변경"
                    sx={{
                        mt: 1.5, py: 0.7, fontSize: 12,
                        bgcolor: "rgba(255,255,255,0.14)", color: "#fff",
                        border: "1px solid rgba(255,255,255,0.28)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                    }}
                />
            </Box>
        </Paper>
    );
}

function KvRow({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 11.5, opacity: 0.75 }}>{label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{value}</Typography>
        </Stack>
    );
}

/* ─── 라벨 변환 헬퍼 ────────────────────────────────────── */
function koStatus(s) {
    switch (s) {
        case "PENDING":     return "대기";
        case "IN_PROGRESS": return "진행";
        case "APPROVED":    return "완료";
        case "REJECTED":    return "반려";
        case "HOLD":        return "보류";
        case "DRAFT":       return "임시";
        default:            return s || "-";
    }
}
function shortChannelType(t) {
    switch (t) {
        case "PROJECT": return "PJT";
        case "TEAM":    return "팀";
        case "NOTICE":  return "공지";
        case "OTHER":   return "기타";
        default:        return "—";
    }
}
